import React from 'react'
import { BLOCKS, Document } from '@contentful/rich-text-types'
import { useUserJourneyService } from '../../../lib/hooks'
import { ItemCtaGroup } from '@components/item/cta-group'
import { FeatureHighlight } from '@components/item/feature-highlight'
import { ComponentCtaButtonType, ComponentCtaButtonColor, CtaGroupLayout } from '../../../lib/types'
import { TitleSize } from '../../../lib/types/typography'
import { ComponentContentTypes } from '../../../lib/types/content-types'
import { createEntry } from '../../../lib/utils/contentful-entry'
import { getConfig } from 'src/config'

const createRichText = (text: string, bold = false): Document => ({
  data: {},
  content: [
    {
      data: {},
      content: [
        {
          data: {},
          marks: bold ? [{ type: 'bold' }] : [],
          value: text,
          nodeType: 'text',
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  nodeType: BLOCKS.DOCUMENT,
})

const getPersonalizedCopy = (hasLlama: boolean, hasExtension: boolean) => {
  if (hasLlama && hasExtension) {
    return {
      title: 'Access More Features in Grammarly Editor',
      description: 'Check grammar, rewrite sentences —all for free— when you write documents in Grammarly Editor.',
    }
  }

  if (hasLlama || hasExtension) {
    const platform = hasLlama ? 'web browser' : 'desktop'
    return {
      title: 'Start Writing With Grammarly',
      description: `You've signed up for Grammarly. The next step is to get Grammarly up and running on your ${platform} to get real-time feedback as you write.`,
    }
  }

  return {
    title: 'Start Writing With Grammarly',
    description:
      "You've signed up for Grammarly. The next step is to get Grammarly up and running on your desktop or web browser to get real-time feedback as you write.",
  }
}

const createPersonalizedCtaGroup = (hasLlama: boolean, hasExtension: boolean) => {
  const config = getConfig()
  const ctAs = []

  if (hasLlama && hasExtension) {
    ctAs.push(
      createEntry(ComponentContentTypes.ComponentCtaButton, 'EditorCta', {
        ctaText: createRichText('Start writing', true),
        internalName: 'EditorCta',
        color: ComponentCtaButtonColor.WhiteTextGreenButton,
        type: ComponentCtaButtonType.Link,
        targetLink: createEntry(ComponentContentTypes.ComponentLink, 'EditorCtaLink', {
          targetUrl: config.urls.aiEditorNewDoc,
          targetTab: 'New ( _blank )',
          internalName: 'EditorCtaLink',
          appendCurrentQueryParams: true,
        }),
      })
    )
  } else {
    if (!hasLlama) {
      ctAs.push(
        createEntry(ComponentContentTypes.ComponentCtaButton, 'DownloadDesktopAppCta', {
          ctaText: createRichText('Download desktop app', true),
          internalName: 'DownloadDesktopAppCta',
          color: ComponentCtaButtonColor.WhiteTextGreenButton,
          type: ComponentCtaButtonType.LlamaInstall,
        })
      )
    }
    if (!hasExtension) {
      ctAs.push(
        createEntry(ComponentContentTypes.ComponentCtaButton, 'InstallBrowserExtensionCta', {
          ctaText: createRichText('Install browser extension', true),
          internalName: 'InstallBrowserExtensionCta',
          color: !hasLlama ? ComponentCtaButtonColor.Secondary : ComponentCtaButtonColor.WhiteTextGreenButton,
          type: ComponentCtaButtonType.ExtensionInstall,
        })
      )
    }
  }

  return createEntry(ComponentContentTypes.ItemCtaGroup, 'PersistentClientCtaSection', {
    internalName: 'PersistentClientCtaSection',
    layout: CtaGroupLayout.Row,
    ctAs,
  })
}

export const PersistentClientCtaSection: React.FC = () => {
  const userJourneyService = useUserJourneyService()

  const hasLlama = userJourneyService.hasLlamaInstalled
  const hasExtension = userJourneyService.hasExtensionInstalled

  const copy = getPersonalizedCopy(hasLlama, hasExtension)
  const ctaGroup = createPersonalizedCtaGroup(hasLlama, hasExtension)

  const featureHighlight = createEntry(
    ComponentContentTypes.ItemFeatureHighlight,
    'PersistentClientSectionFeatureHighlight',
    {
      internalName: 'PersistentClientSectionFeatureHighlight',
      title: createRichText(copy.title),
      description: createRichText(copy.description),
      titleSize: TitleSize.HXL,
    }
  )

  return (
    <>
      <FeatureHighlight structure={featureHighlight} />
      <ItemCtaGroup structure={ctaGroup} />
    </>
  )
}
